import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import SectionTrail from '../SectionTrail/SectionTrail';
import { contactListSelector } from '../../scenes/Organisation/scenes/Contact/ContactSelectors';
import { customerListSelector } from '../../scenes/Organisation/scenes/Customer/CustomerSelectors';
import { supplierListSelector } from '../../scenes/Organisation/scenes/Supplier/SupplierSelectors';
import { latestPolicySelector } from '../../scenes/Organisation/scenes/Policy/PolicySelectors';
import { orgIdSelector } from '../../scenes/Organisation/OrganisationSelectors';
import { fetchContact } from '../../scenes/Organisation/scenes/Contact/ContactAction';
import { fetchCustomer } from '../../scenes/Organisation/scenes/Customer/CustomerAction';
import { fetchSupplier } from '../../scenes/Organisation/scenes/Supplier/SupplierAction';
import { fetchStrategicThirdParty } from '../../scenes/Organisation/scenes/StrategicThirdParty/StrategicThirdPartyAction';
import { fetchProduct } from '../../scenes/Organisation/scenes/Product/ProductAction';
import { fetchLatestPolicy } from '../../scenes/Organisation/scenes/Policy/PolicyAction';

const ORG_SECTIONS = [
  { key: 'contacts', label: 'Internal Contacts', short: 'Contacts', route: '/organization/contacts', color: '#5bc2e7' },
  {
    key: 'customers',
    label: 'Strategic Customers',
    short: 'Customers',
    route: '/organization/customers',
    color: '#343579',
  },
  {
    key: 'suppliers',
    label: 'Strategic Suppliers',
    short: 'Suppliers',
    route: '/organization/suppliers',
    color: '#a8c08f',
  },
  {
    key: 'strategic-third-parties',
    label: 'Strategic Third Parties',
    short: '3rd Party',
    route: '/organization/strategic-third-parties',
    color: '#f1c400',
  },
  {
    key: 'strategic-crisis-experts',
    label: 'Strategic Crisis Experts',
    short: 'Experts',
    route: '/organization/strategic-crisis-experts',
    color: '#cb2c30',
  },
  {
    key: 'strategic-areas',
    label: 'Strategic Areas',
    short: 'Assets',
    route: '/organization/strategic-areas',
    color: '#6366f1',
  },
  { key: 'policy', label: 'Policy Programme', short: 'Policy', route: '/organization/policy', color: '#00685e' },
];

const CRISIS_EXPERT_CLASS = 'Strategic Crisis Experts';

function OrgSectionTrail({
  location,
  completionMap,
  orgId,
  uninitializedKeys,
  loading,
  fetchContact,
  fetchCustomer,
  fetchSupplier,
  fetchStrategicThirdParty,
  fetchProduct,
  fetchLatestPolicy,
}) {
  useEffect(() => {
    if (!orgId) return;
    if (uninitializedKeys.contacts) fetchContact(orgId);
    if (uninitializedKeys.customers) fetchCustomer(orgId);
    if (uninitializedKeys.suppliers) fetchSupplier(orgId);
    if (uninitializedKeys.thirdParties) fetchStrategicThirdParty(orgId);
    if (uninitializedKeys.products) fetchProduct(orgId);
    if (uninitializedKeys.policy) fetchLatestPolicy(orgId);
  }, [orgId]);

  const currentIndex = ORG_SECTIONS.findIndex((s) => location.pathname.startsWith(s.route));
  if (currentIndex === -1) return null;

  const sections = ORG_SECTIONS.map((section, i) => {
    let status;
    if (i === currentIndex) {
      status = 'active';
    } else if (completionMap[section.key]) {
      status = 'complete';
    } else {
      status = 'incomplete';
    }
    return { ...section, status };
  });

  return <SectionTrail sections={sections} ariaLabel="Organisation setup progress" loading={loading} />;
}

const mapStateToProps = (state) => {
  const contactList = contactListSelector(state) || [];
  const customerList = customerListSelector(state) || [];
  const supplierList = supplierListSelector(state) || [];
  const latestPolicy = latestPolicySelector(state);
  const rawThirdPartyList = state.strategicThirdPartyReducer?.strategicThirdPartyList || [];
  const rawProducts = state.productReducer?.products || [];

  const hasThirdParties = rawThirdPartyList.some(
    (item) => item?.strategicThirdPartyCategory?.strategicThirdPartyClass !== CRISIS_EXPERT_CLASS,
  );
  const hasCrisisExperts = rawThirdPartyList.some(
    (item) => item?.strategicThirdPartyCategory?.strategicThirdPartyClass === CRISIS_EXPERT_CLASS,
  );

  const cr = state.contactReducer;
  const cur = state.customerReducer;
  const sr = state.supplierReducer;
  const stp = state.strategicThirdPartyReducer;
  const pr = state.productReducer;
  const pol = state.policyReducer;

  return {
    orgId: orgIdSelector(state),
    loading: Boolean(
      cr?.loadingContacts ||
      cur?.loadingCustomers ||
      sr?.loadingSuppliers ||
      stp?.loadingStrategicThirdParty ||
      pr?.loadingStrategicAreas ||
      pol?.loadingLatestPolicy,
    ),
    uninitializedKeys: {
      contacts: cr?.contactList === undefined && !cr?.loadingContacts,
      customers: cur?.customerList === undefined && !cur?.loadingCustomers,
      suppliers: sr?.supplierList === undefined && !sr?.loadingSuppliers,
      thirdParties: stp?.strategicThirdPartyList === undefined && !stp?.loadingStrategicThirdParty,
      products: pr?.products === undefined && !pr?.loadingStrategicAreas,
      policy: pol?.latestPolicy === undefined && !pol?.loadingLatestPolicy,
    },
    completionMap: {
      contacts: contactList.length > 0,
      customers: customerList.length > 0,
      suppliers: supplierList.length > 0,
      'strategic-third-parties': hasThirdParties,
      'strategic-crisis-experts': hasCrisisExperts,
      'strategic-areas': rawProducts.length > 0,
      policy: Boolean(latestPolicy),
    },
  };
};

const mapDispatchToProps = {
  fetchContact,
  fetchCustomer,
  fetchSupplier,
  fetchStrategicThirdParty,
  fetchProduct,
  fetchLatestPolicy,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(OrgSectionTrail));
